<template>
  <div class="flex h-full flex-col">
    <div class="relative grow">
      <Dashboard
        :uppy="uppy"
        :props="dashboard_props"
      />

      <div
        v-if="is_disabled"
        class="absolute inset-0 flex cursor-pointer items-center justify-center rounded-lg bg-gray-100"
        @click="$emit('openCategory')"
      >
        <div class="uppy-category-overlay-content">
          <h5 class="text-lg font-semibold">
            {{ t('upload.labels.chooseCategory') }}
          </h5>
        </div>
      </div>
    </div>
    <Teleport to="body">
      <div
        v-show="is_dragging_over"
        class="uppy-drop-overlay"
      >
        <div class="uppy-drop-overlay-content">
          <FontAwesomeIcon
            :icon="['fal', 'file-plus']"
            class="mb-2"
            size="3x"
          />
          <p class="mt-4 text-xl font-semibold">
            {{ container_text || t('upload.labels.defaultMessage') }}
          </p>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import Uppy from '@uppy/core';
import AwsS3 from '@uppy/aws-s3';
import Dashboard from '@uppy/vue/dashboard';
import Webcam from '@uppy/webcam';
import ScreenCapture from '@uppy/screen-capture';
import DropTarget from '@uppy/drop-target';
import { useI18n } from 'vue-i18n';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import useUppyUpload from '@/composables/useUppyUpload.js';

import '@uppy/core/css/style.min.css';
import '@uppy/dashboard/css/style.min.css';
import '@uppy/webcam/css/style.min.css';
import '@uppy/screen-capture/css/style.min.css';
import { mapActions } from '@/store/lib/vue3helpers.js';
import { MAX_FILE_SIZE } from '@/app/data/document_constants.js';
import { formatFileSize } from '@/app/util/formatting_functions.js';

const props = defineProps({
  category_required: { type: Boolean, default: false },
  auto_process_queue: { type: Boolean, default: true },
  images_only: { type: Boolean, default: false },
  is_start_upload: { type: Boolean, default: false },
  max_files: { type: Number, default: 30 },
  max_files_size: { type: Number, default: MAX_FILE_SIZE }, // in bytes
  params: { type: Object, default: () => ({}) },
  remove_after_upload: { type: Boolean, default: false },
  is_template: { type: Boolean, default: false },
  container_text: { type: String, default: '' },
  allowed_file_types: { type: Array, default: null },
});

const emit = defineEmits([
  'uploadSuccess',
  'uploadsSuccess',
  'fileAdded',
  'openCategory',
  'uploadDenied',
  'uploadFailed',
  'uploadError',
  'fileRemoved',
  'sending',
]);

const { t } = useI18n();
const { getMetaParams, getHeaders, getUppyLocale } = useUppyUpload();
const is_dragging_over = ref(false);
const files_added_this_drop = new Set();

const { UPDATE_ATTACHMENT, UPDATE_ATTACHMENT_TEMPLATE, CREATE_ATTACHMENT, CREATE_ATTACHMENT_TEMPLATE } = mapActions([
  'UPDATE_ATTACHMENT',
  'UPDATE_ATTACHMENT_TEMPLATE',
  'CREATE_ATTACHMENT',
  'CREATE_ATTACHMENT_TEMPLATE',
]);

const note_text = computed(() => {
  let note = t('upload.labels.warningMaxFileSize', {
    max_files_size: formatFileSize(props.max_files_size, 0, 1024),
  });
  if (props.allowed_file_types) {
    note += ` (${props.allowed_file_types.join(', ')})`;
  } else if (props.images_only) {
    note += ' (jpeg, jpg, png, gif)';
  }
  return note;
});

const is_disabled = computed(() => props.category_required && !props.params.attachment_category_id);

const dashboard_props = computed(() => ({
  inline: true,
  theme: 'light',
  width: '100%',
  height: '350px',
  disabled: is_disabled.value,
  showProgressDetails: true,
  hideAfterFinish: false,
  hideUploadButton: props.auto_process_queue,
  showRemoveButtonAfterComplete: true,
  doneButtonHandler: null,
  note: note_text.value,
  proudlyDisplayPoweredByUppy: false,
}));

const uppy = new Uppy({
  autoProceed: props.auto_process_queue,
  locale: getUppyLocale(),
  restrictions: {
    maxFileSize: props.max_files_size,
    maxNumberOfFiles: props.max_files,
    allowedFileTypes: props.allowed_file_types || (props.images_only ? ['image/*'] : null),
  },
  onBeforeFileAdded: (file) => {
    if (!file.name.includes('.')) return false;
    const file_key = `${file.name}_${file.size}_${file.lastModified}`;
    if (files_added_this_drop.has(file_key)) return false;
    files_added_this_drop.add(file_key);
    file.id = `${file.name}_${file.size}_${file.lastModified}_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    return true;
  },
  meta: getMetaParams(props.params),
});

uppy.use(AwsS3, {
  limit: 5,
  shouldUseMultipart: false,
  async getUploadParameters(file) {
    try {
      const parts = file.name.split('.');
      const file_ext = parts.length > 1 ? parts.pop() : '';
      const filename = parts.join('.');
      const file_mime_type = file.type || 'application/octet-stream';
      const attributes = {
        name: filename,
        file_mime_type: file_mime_type,
        file_ext: file_ext,
        file_size: file.size,
        attachment_category_id: props.params.attachment_category_id,
      };

      const attachment = props.is_template
        ? await CREATE_ATTACHMENT_TEMPLATE({ record: { attributes } })
        : await CREATE_ATTACHMENT({ record: { attributes } });

      const { url, method, headers } = attachment.meta;

      uppy.setFileMeta(file.id, {
        attachment: attachment,
      });

      return {
        method: method || 'PUT',
        url: url,
        headers: headers || {
          'Content-Type': file_mime_type,
        },
      };
    } catch (error) {
      emit('uploadError', file, error);
      throw error;
    }
  },
});

uppy.use(Webcam, {
  modes: props.images_only ? ['picture'] : ['picture', 'video-audio'],
  mirror: true,
  facingMode: 'environment',
  showRecordingLength: true,
  showVideoSourceDropdown: true,
});

uppy.use(DropTarget, {
  target: document.body,
  onDragOver: () => {
    is_dragging_over.value = true;
  },
  onDragLeave: () => {
    is_dragging_over.value = false;
  },
  onDrop: () => {
    is_dragging_over.value = false;
    files_added_this_drop.clear();
  },
});

if (!props.images_only) {
  uppy.use(ScreenCapture);
}

uppy.on('file-added', (file) => {
  emit('fileAdded', file);
});

uppy.on('file-removed', (file) => {
  if (!file.response || !file.response.body) return;
  emit('fileRemoved', file.response.body.data);
});

uppy.on('upload', (data) => {
  emit('sending', data);
});

uppy.on('upload-success', (file) => {
  const attachment = file.meta.attachment;
  const attachment_id = attachment.id;
  if (props.remove_after_upload) {
    uppy.removeFile(file.id);
  }
  try {
    file.id = attachment_id;
    if (props.is_template)
      UPDATE_ATTACHMENT_TEMPLATE({
        record: { id: attachment_id, attributes: { uploaded_at: new Date().toISOString() } },
      });
    else UPDATE_ATTACHMENT({ record: { id: attachment_id, attributes: { uploaded_at: new Date().toISOString() } } });
  } catch (error) {
    Sentry.captureException(error, {
      extra: {
        message:
          'This happened while trying to update the attachment uploaded at date for version creation in uppyupload',
      },
    });
    emit('uploadError', file, error);
  }

  emit('uploadSuccess', file, { data: attachment });

  files_added_this_drop.clear();
});

uppy.on('upload-error', (file, error, response) => {
  emit('uploadError', file, error, response);
});

uppy.on('complete', (result) => {
  if (result.failed.length > 0) {
    emit('uploadFailed', true);
  } else {
    emit('uploadFailed', false);
    emit('uploadsSuccess', result);
  }
});

onBeforeUnmount(() => {
  if (uppy) {
    uppy.destroy();
  }
});

watch(
  () => props.is_start_upload,
  (newValue) => {
    if (newValue) {
      uppy.upload();
    }
  },
);

watch(
  () => props.params,
  (_newParams) => {
    uppy.setMeta(getMetaParams());
  },
  { deep: true },
);

watch(
  () => is_disabled.value,
  (new_value) => {
    uppy.getPlugin('Dashboard').setOptions({ disabled: new_value });

    if (new_value) {
      if (uppy.getPlugin('DropTarget')) {
        uppy.removePlugin(uppy.getPlugin('DropTarget'));
      }
    } else if (!uppy.getPlugin('DropTarget')) {
      uppy.use(DropTarget, {
        target: document.body,
        onDragOver: () => {
          is_dragging_over.value = true;
        },
        onDragLeave: () => {
          is_dragging_over.value = false;
        },
        onDrop: () => {
          is_dragging_over.value = false;
          files_added_this_drop.clear();
        },
      });
    }
  },
);
</script>

<style scoped>
.uppy-drop-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(66, 153, 225, 0.3);
  border: 4px dashed #4299e1;
  box-sizing: border-box;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.uppy-drop-overlay-content {
  text-align: center;
  color: #ffffff;
  padding: 2rem;
  border-radius: 0.5rem;
  background-color: rgba(0, 0, 0, 0.3);
  border: 3px dashed #fff;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  z-index: 99999;
}

.uppy-drop-overlay .icon {
  color: #4299e1;
}

button.uppy-u-reset.uppy-c-btn.uppy-Dashboard-browse {
  font-weight: 500;
}
</style>
<style>
.uppy-Dashboard-inner:has(#uppy-DashboardContent-panel--Webcam),
.uppy-Dashboard-inner:has(#uppy-DashboardContent-panel--ScreenCapture) {
  height: 550px !important;
}
.uppy-Dashboard-browse {
  color: #096553;
}
.uppy-DashboardTab-iconMyDevice,
.uppy-DashboardTab-inner svg path,
.uppy-DashboardTab-iconScreenRec {
  color: #fff;
  fill: #fff;
}
.uppy-DashboardTab-inner {
  background: #096553;
}
.uppy-Dashboard-AddFiles-title,
.uppy-Dashboard-AddFiles-list,
.uppy-Dashboard-AddFiles-info {
  font-family: var(--bulma-family-sans-serif) !important;
}
</style>
